// The Voices card of the identity page, connected (ADR-0077): the read, the reference switch,
// *Show in graph* and each voiceprint's player. The samples still on their way come from the
// audio tab's read, which the host hands in; once one of them has been checked, the card reads
// again, so a new voiceprint appears without a second poll.
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  IdentityVoicesCard,
  type PendingSampleRow,
} from '@/components/identities/voices/IdentityVoicesCard'
import { VoiceClipPlayback } from '@/features/voices/VoiceClipPlayer'
import type { IdentityVoiceprint } from '@/services/api/identity-voices'
import { voiceKeys } from './keys'
import { useIdentityVoices } from './useIdentityVoices'

const COPY = 'identity.voices'

function VoiceprintPlayer({ voiceprint }: { voiceprint: IdentityVoiceprint }) {
  const { t } = useTranslation()
  if (!voiceprint.audio_url)
    return <p className="text-sm text-muted-foreground">{t(`${COPY}.noClip`)}</p>
  return <VoiceClipPlayback url={voiceprint.audio_url} typedAs={voiceprint.audio_name} />
}

/** Read the card again once a sample the worker was checking has been checked. */
function useRereadWhenChecked(
  caseId: string,
  identityId: string,
  pendingSamples: PendingSampleRow[]
) {
  const queryClient = useQueryClient()
  const checking = pendingSamples
    .filter(sample => sample.state === 'processing')
    .map(sample => sample.capture_id)
    .join('|')
  const before = useRef(checking)
  useEffect(() => {
    if (before.current && before.current !== checking)
      void queryClient.invalidateQueries({ queryKey: voiceKeys.identity(caseId, identityId) })
    before.current = checking
  }, [caseId, identityId, checking, queryClient])
}

export function IdentityVoices({
  caseId,
  identityId,
  pendingSamples,
}: {
  caseId: string
  identityId: string
  pendingSamples: PendingSampleRow[]
}) {
  const voices = useIdentityVoices(caseId, identityId)
  useRereadWhenChecked(caseId, identityId, pendingSamples)
  return (
    <IdentityVoicesCard
      voiceprints={voices.data?.voiceprints ?? []}
      pendingSamples={pendingSamples}
      state={{
        pendingId: voices.pendingId,
        isLoading: voices.isLoading,
        errorKey: voices.errorKey,
      }}
      actions={{ onToggleReference: voices.onToggleReference, onShowInGraph: voices.onShowInGraph }}
      player={voiceprint => <VoiceprintPlayer voiceprint={voiceprint} />}
    />
  )
}
